<template>
  <div class="geo-data-revision header-space">
    <div class="geo-data-revision__inner">
      <header class="page-header">
        <div class="page-header__text">
          <p class="page-kicker">{{ t('kicker') }}</p>
          <h1>{{ t('title') }}</h1>
          <p class="page-lead">{{ t('intro') }}</p>
        </div>
        <div class="language-selector" role="group" :aria-label="t('languageAria')">
          <button
            type="button"
            class="lang-btn"
            :class="{ 'lang-btn--active': lang === 'en' }"
            :aria-pressed="lang === 'en' ? 'true' : 'false'"
            :aria-label="t('switchToEn')"
            @click="setLang('en')"
          >
            EN
          </button>
          <button
            type="button"
            class="lang-btn"
            :class="{ 'lang-btn--active': lang === 'de' }"
            :aria-pressed="lang === 'de' ? 'true' : 'false'"
            :aria-label="t('switchToDe')"
            @click="setLang('de')"
          >
            DE
          </button>
        </div>
      </header>

      <section class="info-card">
        <p class="info-card__title">{{ t('prerequisites') }}</p>
        <ul class="info-card__list">
          <li>{{ t('prereqAddress') }}</li>
          <li>{{ t('prereqApproved') }}</li>
          <li>{{ t('prereqIncomplete') }}</li>
        </ul>
        <p class="info-card__note">{{ t('note') }}</p>
      </section>

      <section class="toolbar-card">
        <div class="filter-grid">
          <div class="geo-filter-field">
            <div class="geo-filter-field__head">
              <span class="geo-filter-field__label" id="geo-filter-entity-label">{{ t('entity') }}</span>
            </div>
            <div class="geo-filter-field__control">
              <v-select
                v-model="entity"
                :items="entityOptions"
                :menu-props="menuProps"
                :aria-labelledby="'geo-filter-entity-label'"
                dense
                solo
                flat
                hide-details
                @change="onEntityChange"
              />
            </div>
          </div>

          <div class="geo-filter-field">
            <div class="geo-filter-field__head">
              <span class="geo-filter-field__label" id="geo-filter-group-label">{{ t('group') }}</span>
              <span class="geo-filter-field__badge">{{ t('optional') }}</span>
              <v-tooltip bottom max-width="280" color="grey darken-3">
                <template v-slot:activator="{ on }">
                  <button
                    type="button"
                    class="geo-filter-field__tip"
                    v-on="on"
                    :aria-label="t('groupHelp')"
                  >
                    <v-icon small>mdi-information-outline</v-icon>
                  </button>
                </template>
                <span>{{ t('groupHelp') }}</span>
              </v-tooltip>
            </div>
            <div
              class="geo-filter-field__control"
              :class="{ 'geo-filter-field__control--invalid': !!groupFieldError }"
            >
              <v-combobox
                v-model="groupModel"
                :items="groupOptions"
                item-text="text"
                item-value="value"
                :menu-props="menuProps"
                :aria-labelledby="'geo-filter-group-label'"
                clearable
                dense
                solo
                flat
                hide-details
                :return-object="false"
              />
            </div>
            <p v-if="groupFieldError" class="geo-filter-field__message geo-filter-field__message--error">
              {{ groupFieldError }}
            </p>
            <p v-else-if="groupFieldWarning" class="geo-filter-field__message geo-filter-field__message--warn">
              {{ groupFieldWarning }}
            </p>
          </div>

          <div class="geo-filter-field">
            <div class="geo-filter-field__head">
              <span class="geo-filter-field__label" id="geo-filter-year-label">{{ t('year') }}</span>
              <span class="geo-filter-field__badge">{{ t('optional') }}</span>
              <v-tooltip bottom max-width="280" color="grey darken-3">
                <template v-slot:activator="{ on }">
                  <button
                    type="button"
                    class="geo-filter-field__tip"
                    v-on="on"
                    :aria-label="t('yearHelp')"
                  >
                    <v-icon small>mdi-information-outline</v-icon>
                  </button>
                </template>
                <span>{{ t('yearHelp') }}</span>
              </v-tooltip>
            </div>
            <div
              class="geo-filter-field__control"
              :class="{ 'geo-filter-field__control--invalid': !!yearFieldError }"
            >
              <v-combobox
                v-model="yearModel"
                :items="yearOptions"
                :menu-props="menuProps"
                :aria-labelledby="'geo-filter-year-label'"
                clearable
                dense
                solo
                flat
                hide-details
              />
            </div>
            <p v-if="yearFieldError" class="geo-filter-field__message geo-filter-field__message--error">
              {{ yearFieldError }}
            </p>
          </div>

          <div class="geo-filter-field">
            <div class="geo-filter-field__head">
              <span class="geo-filter-field__label" id="geo-filter-target-label">{{ t('targetSet') }}</span>
            </div>
            <div class="geo-filter-field__control">
              <v-select
                v-model="missingOnly"
                :items="missingOptions"
                :menu-props="menuProps"
                :aria-labelledby="'geo-filter-target-label'"
                dense
                solo
                flat
                hide-details
              />
            </div>
          </div>

          <div class="geo-filter-field geo-filter-field--narrow">
            <div class="geo-filter-field__head">
              <label class="geo-filter-field__label" for="geo-filter-limit">{{ t('limit') }}</label>
              <v-tooltip bottom max-width="240" color="grey darken-3">
                <template v-slot:activator="{ on }">
                  <button
                    type="button"
                    class="geo-filter-field__tip"
                    v-on="on"
                    :aria-label="t('limitHelp')"
                  >
                    <v-icon small>mdi-information-outline</v-icon>
                  </button>
                </template>
                <span>{{ t('limitHelp') }}</span>
              </v-tooltip>
            </div>
            <input
              id="geo-filter-limit"
              v-model.number="debugLimit"
              class="filter-input"
              :class="{ 'filter-input--invalid': !!limitFieldError }"
              type="number"
              min="1"
              step="1"
            >
            <p v-if="limitFieldError" class="geo-filter-field__message geo-filter-field__message--error">
              {{ limitFieldError }}
            </p>
          </div>

          <div class="geo-filter-field geo-filter-field--action">
            <div class="geo-filter-field__head geo-filter-field__head--spacer" aria-hidden="true">
              <span class="geo-filter-field__label">&nbsp;</span>
            </div>
            <button
              type="button"
              class="admin-btn admin-btn--primary"
              :disabled="loading"
              @click="onLoadOverview"
            >
              {{ loading ? t('pleaseWait') : t('loadOverview') }}
            </button>
          </div>
        </div>

        <p v-if="scopeHint" class="filter-scope-hint" :class="scopeHintClass">
          {{ scopeHint }}
        </p>
      </section>

      <div v-show="initiated" class="page-results">
        <section class="results-card">
          <div class="results-card__head">
            <h2>{{ t('colName') }}</h2>
          </div>
          <div class="table-scroll">
            <table class="data-table">
              <thead>
                <tr>
                  <th>{{ t('colName') }}</th>
                  <th>{{ t('colValue') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="metric in geoDataInformation" :key="metricRowKey(metric)">
                  <td>{{ metricLabel(metric) }}</td>
                  <td class="data-table__value">{{ metric.value }}</td>
                </tr>
                <tr v-if="!geoDataInformation || !geoDataInformation.length">
                  <td colspan="2" class="data-table__empty">—</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <div class="action-row">
          <button
            type="button"
            class="admin-btn admin-btn--primary"
            @click="onBulkRevise"
            :disabled="geoDataLoading"
          >
            {{ geoDataLoading ? t('pleaseWait') : t('reviseGeo') }}
          </button>
          <button
            type="button"
            class="admin-btn admin-btn--ghost"
            :disabled="debugLoading"
            @click="onDebugQualification"
          >
            {{ debugLoading ? t('pleaseWait') : t('debugQualification') }}
          </button>
        </div>

        <section v-if="debugRows && debugRows.length" class="results-card">
          <div class="results-card__head">
            <h2>{{ t('debugQualification') }}</h2>
          </div>
          <div class="table-scroll table-scroll--wide">
            <table class="data-table">
              <thead>
                <tr>
                  <th>{{ t('colType') }}</th>
                  <th>{{ t('colId') }}</th>
                  <th>{{ t('colApproved') }}</th>
                  <th>{{ t('colZip') }}</th>
                  <th>{{ t('colCity') }}</th>
                  <th>{{ t('colCountryCode') }}</th>
                  <th>{{ t('colStreet') }}</th>
                  <th>{{ t('colLat') }}</th>
                  <th>{{ t('colLng') }}</th>
                  <th>hasZipCode</th>
                  <th>hasCity</th>
                  <th>hasCountryCode</th>
                  <th>hasStreet</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in debugRows" :key="(row.entityType || 'x') + '-' + row.id">
                  <td>{{ row.entityType || '—' }}</td>
                  <td>{{ row.id }}</td>
                  <td>{{ row.isApproved ? t('yes') : t('no') }}</td>
                  <td>{{ row.zipCode || '—' }}</td>
                  <td>{{ row.city || '—' }}</td>
                  <td>{{ row.countryCode || '—' }}</td>
                  <td>{{ row.street || '—' }}</td>
                  <td>{{ row.latitude !== null && row.latitude !== undefined ? row.latitude : '—' }}</td>
                  <td>{{ row.longitude !== null && row.longitude !== undefined ? row.longitude : '—' }}</td>
                  <td>{{ row.hasZipCode ? t('yes') : t('no') }}</td>
                  <td>{{ row.hasCity ? t('yes') : t('no') }}</td>
                  <td>{{ row.hasCountryCode ? t('yes') : t('no') }}</td>
                  <td>{{ row.hasStreet ? t('yes') : t('no') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
        <p v-else-if="debugLoadedOnce && !debugLoading" class="empty-hint">
          {{ t('debugEmpty') }}
        </p>

        <v-dialog v-model="geoDataDialog" max-width="960px" content-class="admin-dialog" persistent>
          <v-card class="admin-dialog__card">
            <v-card-title class="admin-dialog__title">
              {{ reviseMode === 'dry-run' ? t('dryRunTitle') : t('importTitle') }}
            </v-card-title>
            <v-card-text class="admin-dialog__body">
              <div v-if="reviseMode === 'dry-run'">
                <div v-if="dryRunSummary && !dryRunSummary.error">
                  <p class="mb-2">
                    <strong>{{ t('scope') }}:</strong>
                    {{ t('entity') }}: {{ dryRunSummary.scope?.entity || entity }},
                    {{ t('groupLabel') }}: {{ dryRunSummary.scope?.group || '—' }},
                    {{ t('yearLabel') }}: {{ dryRunSummary.scope?.year || '—' }}
                  </p>
                  <p class="mb-2" v-if="dryRunSummary.counts && !Array.isArray(dryRunSummary.counts)">
                    <strong>{{ t('accounts') }}:</strong>
                    {{ formatOverviewSnippet(dryRunSummary.counts.accounts) }}
                    <br>
                    <strong>{{ t('contacts') }}:</strong>
                    {{ formatOverviewSnippet(dryRunSummary.counts.contacts) }}
                  </p>
                  <p class="mb-2" v-else>
                    <strong>{{ t('counts') }}:</strong>
                    {{ t('total') }}: {{ dryRunSummary.counts?.total ?? '—' }},
                    {{ t('missing') }}: {{ dryRunSummary.counts?.missing ?? '—' }}
                  </p>
                  <p class="mb-4">
                    <strong>{{ t('willProcess') }}:</strong>
                    {{ dryRunSummary.will_process ?? '—' }}
                    <span v-if="missingOnly === '1'"> {{ t('missingOnlyHint') }}</span>
                    <span v-else> {{ t('allEntitiesHint') }}</span>
                  </p>
                  <p class="dialog-hint">{{ t('importHint') }}</p>
                </div>
                <div v-else>
                  <p class="dialog-error">
                    {{ t('dryRunFailed') }}: {{ dryRunSummary?.error || t('unknownError') }}
                  </p>
                </div>
              </div>
              <div v-else>
                <div class="mb-3" v-if="reviseRunning">
                  <v-progress-linear indeterminate color="primary" class="mb-2" />
                  <span>{{ t('importRunning') }}</span>
                </div>
                <v-data-table
                  :headers="reviseHeaders"
                  :items="reviseResults"
                  :items-per-page="10"
                  dense
                  class="revise-table"
                  mobile-breakpoint="0"
                >
                  <template v-slot:item.status="{ item }">
                    <v-chip
                      :color="reviseStatusColor(item.status)"
                      small
                      outlined
                    >
                      {{ item.status }}
                    </v-chip>
                  </template>
                </v-data-table>
                <p class="dialog-meta">{{ t('rows') }}: {{ reviseResults.length }}</p>
              </div>
            </v-card-text>
            <v-card-actions class="admin-dialog__actions">
              <template v-if="reviseMode === 'dry-run'">
                <button type="button" class="admin-btn admin-btn--ghost" @click="onCloseDialog">
                  {{ t('close') }}
                </button>
                <button
                  type="button"
                  class="admin-btn admin-btn--primary"
                  :disabled="!!dryRunSummary?.error"
                  @click="onImport"
                >
                  {{ t('import') }}
                </button>
              </template>
              <template v-else>
                <button
                  type="button"
                  class="admin-btn admin-btn--primary"
                  :disabled="reviseRunning"
                  @click="onCloseDialog"
                >
                  {{ t('close') }}
                </button>
              </template>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>
  </div>
</template>

<script>
import { geoDataRevisionT } from '@/i18n/geoDataRevision'
import {
  geoDebugQualificationPath,
  geoOverviewPath,
  geoReviseBulkPath
} from '@/api/geoPaths'

import { readStoredLang, writeStoredLang } from '@/i18n/adminLang'

const LANG_STORAGE_KEY = 'fledw-geo-data-revision-lang'

export default {
  data: () => ({
    initiated: false,
    geoDataInformation: [],
    geoDataDialog: null,
    geoDataLoading: false,
    loading: false,
    entity: 'account',
    group: '',
    year: '',
    lang: readStoredLang(LANG_STORAGE_KEY),
    missingOnly: '1',
    reviseMode: 'idle',
    dryRunSummary: null,
    reviseResults: [],
    reviseRunning: false,
    debugLimit: 10,
    debugRows: [],
    debugLoading: false,
    debugLoadedOnce: false,
    rankingGroups: [],
    showFilterErrors: false,
    menuProps: {
      offsetY: true,
      nudgeBottom: 4,
      maxHeight: 280,
      contentClass: 'geo-filter-menu'
    }
  }),
  methods: {
    t (key) {
      return geoDataRevisionT(this.lang, key)
    },
    markFiltersTouched () {
      this.showFilterErrors = true
    },
    ensureFiltersValid (requireLimit = false) {
      this.markFiltersTouched()
      if (!this.isOverviewScopeValid || !this.isYearFormatValid) {
        return false
      }
      if (requireLimit && !this.isLimitValid) {
        return false
      }
      return true
    },
    onLoadOverview () {
      if (!this.ensureFiltersValid(false)) {
        return
      }
      this.getList()
    },
    onBulkRevise () {
      if (!this.ensureFiltersValid(false)) {
        return
      }
      this.bulkRevise()
    },
    onDebugQualification () {
      if (!this.ensureFiltersValid(true)) {
        return
      }
      this.debugQualification()
    },
    normalizeComboValue (val) {
      if (val == null || val === '') {
        return ''
      }
      if (typeof val === 'object') {
        return String(val.value ?? val.shortCode ?? val.text ?? '').trim()
      }
      return String(val).trim()
    },
    normalizeGroupValue (val) {
      const raw = this.normalizeComboValue(val)
      if (!raw) {
        return ''
      }
      const byValue = this.groupOptions.find(o => o.value === raw)
      if (byValue) {
        return byValue.value
      }
      const byText = this.groupOptions.find(o => o.text === raw)
      if (byText) {
        return byText.value
      }
      const prefixed = raw.match(/^([A-Za-z0-9_-]+)\s+[—–-]/)
      if (prefixed) {
        return prefixed[1]
      }
      return raw
    },
    async loadRankingGroups () {
      try {
        const response = await this.$http.get('/ranking_group/list')
        this.rankingGroups = Array.isArray(response.data) ? response.data : []
      } catch {
        this.rankingGroups = []
      }
    },
    setLang (nextLang) {
      if (this.lang === nextLang) {
        return
      }
      this.lang = nextLang
      writeStoredLang(LANG_STORAGE_KEY, nextLang)
      if (this.initiated && this.isOverviewScopeValid) {
        this.getList()
      }
    },
    onEntityChange () {
      this.debugRows = []
      this.debugLoadedOnce = false
      if (this.initiated && this.isOverviewScopeValid) {
        this.getList()
      }
    },
    reviseStatusColor (status) {
      if (typeof status === 'number') {
        if (status >= 200 && status < 300) {
          return 'green'
        }
        if (status >= 400) {
          return 'red'
        }
        return 'blue'
      }
      if (status === 'updated') {
        return 'green'
      }
      if (status === 'error') {
        return 'red'
      }
      if (status === 'skipped') {
        return 'grey'
      }
      return 'blue'
    },
    formatOverviewSnippet (rows) {
      if (!Array.isArray(rows) || rows.length === 0) {
        return '—'
      }
      return rows.map(r => `${this.metricLabel(r)}: ${r.value}`).join('; ')
    },
    metricRowKey (row) {
      return `${row.family || 'x'}.${row.metric || row.name || ''}`
    },
    metricLabel (row) {
      if (row && row.family && row.metric) {
        return this.t(`metric.${row.family}.${row.metric}`)
      }
      // Back-compat if an older payload still sends display names.
      return row?.name ?? '—'
    },
    async reviseQuery (dryRun) {
      return geoReviseBulkPath(this.$http, {
        group: this.group || '',
        year: this.year || '',
        missing_only: this.missingOnly,
        dry_run: dryRun,
        entity: this.entity
      })
    },
    getList: async function () {
      this.loading = true
      try {
        const path = await geoOverviewPath(this.$http, {
          group: this.group,
          year: this.year,
          entity: this.entity
        })
        const response = await this.$http.get(path)
        this.geoDataInformation = response.data.counts
        this.totals = response.data.totals
      } finally {
        this.loading = false
      }
    },
    bulkRevise: async function () {
      this.loading = true
      this.geoDataLoading = true
      this.geoDataDialog = false
      this.reviseMode = 'idle'
      this.dryRunSummary = null
      this.reviseResults = []
      try {
        const response = await this.$http.get(await this.reviseQuery('1'), {
          responseType: 'text'
        })
        const line = typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data
        this.dryRunSummary = line
        this.reviseMode = 'dry-run'
        this.geoDataDialog = true
      } catch (e) {
        this.dryRunSummary = { error: e.message || this.t('dryRunFailed') }
        this.reviseMode = 'dry-run'
        this.geoDataDialog = true
      } finally {
        this.geoDataLoading = false
        this.loading = false
      }
    },
    onImport: async function () {
      this.reviseMode = 'import'
      this.reviseResults = []
      this.reviseRunning = true
      try {
        const response = await this.$http.get(await this.reviseQuery('0'), {
          responseType: 'text'
        })
        const raw = response.data
        const text = typeof raw === 'string'
          ? raw
          : (raw && typeof raw === 'object') ? JSON.stringify(raw) : String(raw || '')
        text.split('\n').forEach((line) => {
          const trimmed = line.trim()
          if (trimmed) this.handleStreamLine(trimmed)
        })
      } finally {
        this.reviseRunning = false
      }
    },
    handleStreamLine (line) {
      let obj
      try {
        obj = JSON.parse(line)
      } catch {
        obj = { status: 'raw', message: line }
      }
      const entityType = obj.entityType || (obj.contactId ? 'contact' : (obj.accountId ? 'account' : ''))
      const entityId = obj.contactId || obj.accountId || obj.id || ''
      this.reviseResults.push({
        status: obj.status || (obj.dry_run ? 'dry_run' : 'info'),
        entityType,
        entityId,
        message: obj.message || obj.details || '',
        _raw: obj
      })
    },
    onCloseDialog () {
      this.geoDataDialog = false
      this.reviseMode = 'idle'
      this.dryRunSummary = null
      this.reviseResults = []
      this.reviseRunning = false
    },
    async debugQualification () {
      this.debugLoading = true
      this.debugLoadedOnce = true
      this.debugRows = []
      try {
        const path = await geoDebugQualificationPath(this.$http, {
          group: this.group,
          year: this.year,
          limit: this.debugLimit,
          entity: this.entity
        })
        const response = await this.$http.get(path)
        this.debugRows = Array.isArray(response.data) ? response.data : []
      } finally {
        this.debugLoading = false
      }
    }
  },
  computed: {
    groupModel: {
      get () {
        const match = this.groupOptions.find(o => o.value === this.group)
        return match ? match.text : this.group
      },
      set (val) {
        this.group = this.normalizeGroupValue(val)
        if (this.group || this.year) {
          this.showFilterErrors = true
        }
      }
    },
    yearModel: {
      get () {
        return this.year
      },
      set (val) {
        this.year = this.normalizeComboValue(val)
        if (this.group || this.year) {
          this.showFilterErrors = true
        }
      }
    },
    hasGroup () {
      return !!(this.group && this.group.trim().length > 0)
    },
    hasYear () {
      return !!(this.year && this.year.trim().length > 0)
    },
    isYearFormatValid () {
      if (!this.hasYear) {
        return true
      }
      return /^\d{4}$/.test(this.year.trim()) &&
        Number(this.year) >= 1900 &&
        Number(this.year) <= 2100
    },
    isKnownGroup () {
      if (!this.hasGroup || !this.rankingGroups.length) {
        return true
      }
      return this.groupOptions.some(o => o.value.toLowerCase() === this.group.toLowerCase())
    },
    groupFieldError () {
      if (this.hasYear && !this.hasGroup) {
        return this.t('validationGroupRequired')
      }
      return ''
    },
    groupFieldWarning () {
      if (this.groupFieldError || !this.hasGroup || this.isKnownGroup) {
        return ''
      }
      return this.t('validationGroupUnknown')
    },
    yearFieldError () {
      if (this.hasGroup && !this.hasYear) {
        return this.t('validationYearRequired')
      }
      if (this.hasYear && !this.isYearFormatValid) {
        return this.t('validationYearFormat')
      }
      return ''
    },
    limitFieldError () {
      return this.isLimitValid ? '' : this.t('limitRule')
    },
    scopeHint () {
      // Field-level messages already cover these — avoid a duplicate banner.
      if (this.groupFieldError || this.yearFieldError || this.groupFieldWarning) {
        return ''
      }
      if (this.showFilterErrors && !this.isOverviewScopeValid) {
        return this.t('validationScopePair')
      }
      return ''
    },
    scopeHintClass () {
      return 'filter-scope-hint--error'
    },
    entityOptions () {
      return [
        { text: this.t('entityAccount'), value: 'account' },
        { text: this.t('entityContact'), value: 'contact' },
        { text: this.t('entityBoth'), value: 'all' }
      ]
    },
    groupOptions () {
      return this.rankingGroups
        .filter(rg => rg && rg.shortCode)
        .map(rg => ({
          text: rg.name ? `${rg.shortCode} — ${rg.name}` : rg.shortCode,
          value: rg.shortCode
        }))
        .sort((a, b) => a.value.localeCompare(b.value, undefined, { sensitivity: 'base' }))
    },
    yearOptions () {
      const startYear = new Date().getFullYear() + 1
      return Array.from({ length: 11 }, (_, i) => String(startYear - i))
    },
    missingOptions () {
      return [
        { text: this.t('missingOnly'), value: '1' },
        { text: this.t('allSelected'), value: '0' }
      ]
    },
    reviseHeaders () {
      return [
        { text: this.t('colStatus'), value: 'status' },
        { text: this.t('colType'), value: 'entityType' },
        { text: this.t('colId'), value: 'entityId' },
        { text: this.t('colMessage'), value: 'message' }
      ]
    },
    isOverviewScopeValid () {
      return ((this.hasGroup && this.hasYear) || (!this.hasGroup && !this.hasYear)) &&
        this.isYearFormatValid
    },
    isLimitValid () {
      return Number(this.debugLimit) > 0
    }
  },
  async created () {
    await this.$auth.waitForAuth()
    if (!this.$auth.isAuthenticated) {
      await this.$router.push('/')
      return
    }

    await this.loadRankingGroups()
    await this.getList()
    this.initiated = true
  }
}
</script>
